import { useDroppable } from '@dnd-kit/core'
import type { FC } from 'react'
import { useState } from 'react'
import { STAGE_CONFIG, type ProofPoint, type Stage } from '../domain/Bet.domain'
import { useCreateProofPoint } from '../repositories/useCreateProofPoint'
import { useGetPersons } from '../repositories/useGetPersons'
import { ProofCard } from './ProofCard'

interface Props {
  stage: Stage
  betId: string
  items: ProofPoint[]
  onChangeItem: (proofId: string, updated: ProofPoint) => void
}

export const PipelineStage: FC<Props> = ({
  stage,
  betId,
  items,
  onChangeItem,
}) => {
  const cfg = STAGE_CONFIG[stage]
  const [adding, setAdding] = useState(false)
  const [text, setText] = useState('')
  const [ownerId, setOwnerId] = useState('')
  const { mutate: createProofPoint, isPending } = useCreateProofPoint()
  const { data: persons = [] } = useGetPersons()

  const { setNodeRef, isOver } = useDroppable({
    id: `${betId}-${stage}`,
    data: { stage },
  })

  const handleAdd = () => {
    if (!text.trim()) return
    createProofPoint(
      {
        betId,
        stage,
        text: text.trim(),
        score: 3,
        ownerId: ownerId || undefined,
      },
      {
        onSuccess: () => {
          setText('')
          setOwnerId('')
          setAdding(false)
        },
      }
    )
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        flex: 1,
        minWidth: 0,
        borderRadius: 10,
        transition: 'background 0.15s',
        background: isOver ? `${cfg.accent}18` : 'transparent',
        padding: 0,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: cfg.accent,
          marginBottom: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 5,
        }}
      >
        <span style={{ fontSize: 12 }}>{cfg.icon}</span> {cfg.label} (
        {items.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.length === 0 && (
          <div
            style={{
              fontSize: 12,
              color: '#d1d5db',
              fontStyle: 'italic',
              padding: '8px 10px',
              background: cfg.bg,
              borderRadius: 8,
              border: `1px dashed ${cfg.border}`,
              minHeight: 40,
            }}
          >
            {stage === 'delivered' ? 'No proof points yet' : 'Nothing queued'}
          </div>
        )}
        {items.map((p) => (
          <ProofCard
            key={p.id}
            proof={p}
            stage={stage}
            betId={betId}
            onChange={(updated) => onChangeItem(p.id, updated)}
          />
        ))}

        {/* Add proof point */}
        {adding ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              marginTop: 4,
            }}
          >
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAdd()
                  if (e.key === 'Escape') {
                    setAdding(false)
                    setText('')
                    setOwnerId('')
                  }
                }}
                placeholder="Describe the proof point…"
                style={{
                  flex: 1,
                  fontSize: 12,
                  padding: '5px 8px',
                  borderRadius: 6,
                  border: `1px solid ${cfg.border}`,
                  outline: 'none',
                  background: cfg.bg,
                }}
              />
              <button
                onClick={handleAdd}
                disabled={isPending || !text.trim()}
                style={{
                  fontSize: 11,
                  padding: '5px 8px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  background: cfg.accent,
                  color: '#fff',
                  fontWeight: 600,
                }}
              >
                {isPending ? '…' : 'Add'}
              </button>
            </div>
            <select
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              style={{
                fontSize: 11,
                padding: '4px 6px',
                borderRadius: 6,
                border: `1px solid ${cfg.border}`,
                background: cfg.bg,
                color: '#6b7280',
              }}
            >
              <option value="">Owner (optional)</option>
              {persons.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <button
            onClick={() => setAdding(true)}
            style={{
              marginTop: 4,
              fontSize: 11,
              color: cfg.accent,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px 0',
              fontWeight: 500,
              textAlign: 'left',
            }}
          >
            + Add proof point
          </button>
        )}
      </div>
    </div>
  )
}
